import { useAffiliateCustomizationStore } from '~/store/affiliate-customization'
import { useBorrowerStore } from '~/store/borrower'
import { useDealsStore } from '~/store/deals'
import { useRootStore } from '~/store/root'
import { useApplicationConstants } from '~/composables/useApplicationConstants'
import env from '~/libs/env'
import signup from '~/libs/legacy-redirects/signup.js'

export default defineNuxtRouteMiddleware(async (to) => {
  const nuxtApp = useNuxtApp()
  const { $lendioCookies } = nuxtApp
  const { experienceBaseRoute, singleConfigBaseRoute, nuxtBaseUrl } = useApplicationConstants()
  const piniaRootStore = useRootStore()
  const dealsStore = useDealsStore()
  const borrowerStore = useBorrowerStore()
  const affiliateCustomizationStore = useAffiliateCustomizationStore()
  const isEmbedded = piniaRootStore.isEmbedded;
  const isIL = affiliateCustomizationStore.isIntelligentLendingAffiliate;
  const appCompleteRedirect = getAppCompleteRedirect(isEmbedded, isIL)
  const rules = [
    {
      regExp: /.*\/overview.*/,
      redirect: '/portal'
    },
    {
      regExp: /.*\/continue.*/,
      redirect: '/portal'
    },
    {
      regExp: /.*\/no-cc-options.*/,
      redirect: experienceBaseRoute
    },
    {
      regExp: /.*\/app-complete-landing\/?$/,
      redirect: appCompleteRedirect,
    },
    ...signup,
  ]

  const rule = to.path ? rules.find(testRule.bind(null, to.path)) : null

  if (rule) {
    $lendioCookies.set('referral_url', document.referrer || null)

    const path = typeof rule.redirect === 'object'
      ? rule.redirect.path
      : rule.redirect
    const query = typeof rule.redirect === 'object'
      ? Object.assign({}, to.query, rule.redirect.query || {})
      : to.query

    const redirect = path +'?'+ Object.entries(query).map(([k, v]) => `${k}=${v}`).join(',')

    return navigateTo(redirect, { redirectCode: 301, replace: true })
  }

  const redirectMap = {
    '/business-credit-cards': {
      path: `${env('hpUrl')}/business-loans/business-credit-card/`,
      options: { external: true, replace: true },
    },
    '/covid-relief/ppp-dashboard': {
      path: '/portal/options?currentApplication=\'PPP\'',
      options: { redirectCode: 301, replace: true },
    },
    '/covid-relief/thank-you': {
      path: '/portal/options?currentApplication=\'PPP\'',
      options: { redirectCode: 301, replace: true },
    },
    '/dashboard': {
      path: '/portal',
      options: { redirectCode: 301, replace: true },
    },
    '/update-info': {
      path: '/updated-info',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/offers': {
      path: { path: '/portal/options', query: to.query, hash: to.hash },
      options: { redirectCode: 301, replace: true },
    },
    '/portal/loan-offers': {
      path: { path: '/portal/options', query: to.query, hash: to.hash },
      options: { redirectCode: 301, replace: true },
    },
    '/profile': {
      path: '/portal/settings',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/profile': {
      path: '/portal/settings',
      options: { redirectCode: 301, replace: true },
    },
    '/basic-info': {
      path: { path: `/${experienceBaseRoute}`, query: to.query },
      options: { redirectCode: 301, replace: true },
    },
    '/app': {
      path: `/${experienceBaseRoute}`,
      options: { redirectCode: 301, replace: true },
    },
    '/embedded/app': {
      path: '/embedded',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/business-resources': {
      path: '/portal/explore-other-products',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/credit-cards': {
      path: '/portal/explore-other-products',
      options: { redirectCode: 301, replace: true },
    },
    '/portal/closing': {
      path: '/portal/options',
      options: { redirectCode: 301, replace: true },
    },
    '/intelligent-lending/portal': {
      path: '/portal/options',
      options: { redirectCode: 301, replace: true },
    },
    '/intelligent-lending/portal/dashboard': {
      path: '/portal/options',
      options: { redirectCode: 301, replace: true },
    },
  };

  if (to.path === '/marketplace-app' || to.path.startsWith('/marketplace-app/')) {
    return navigateTo({ path: `/${experienceBaseRoute}`, query: to.query }, { redirectCode: 301, replace: true })
  }

  if (to.path === '/sba-app' || to.path.startsWith('/sba-app/')) {
    return navigateTo({ path: `/${singleConfigBaseRoute}/sba`, query: to.query }, { redirectCode: 301, replace: true })
  }

  if (to.path in redirectMap) {
    const { path, options } = redirectMap[to.path]
    return navigateTo(path, options)
  }

  const legacyILPortalMatch = /.*\/intelligent-lending(\/portal\/.+)$/.exec(to.path)
  if (legacyILPortalMatch) {
    return nuxtApp.runWithContext(() => navigateTo({ path: legacyILPortalMatch[1], query: to.query, hash: to.hash }, { redirectCode: 301, replace: true }))
  }

  if (isIL && /.*\/portal(\/)?$/.exec(to.path)) {
    return nuxtApp.runWithContext(() => navigateTo('/portal/options', { redirectCode: 301, replace: true }))
  }

  if (/\/renewal(\/|$)/.exec(to.path) && piniaRootStore.authUser) {
    if (!dealsStore?.deals) {
      await dealsStore.getDealsWithOffers()
    }

    const hasFundedMPDeal = dealsStore?.deals?.length && dealsStore?.deals?.some(deal => /funded/i.exec(deal.stage))

    if (!hasFundedMPDeal || !borrowerStore?.borrower?.applicationComplete) {
      return navigateTo(experienceBaseRoute)
    }
  }
})

function testRule(path, rule) {
  if (rule.hpRedirect && rule.regExp.test(path)) {
    return rule
  } else {
    return rule.regExp.test(path)
  }
}

function getAppCompleteRedirect(isEmbedded, isIntelligentLending) {
  if (isEmbedded) {
    return '/embedded/portal'
  }

  if (isIntelligentLending) {
    return '/portal/options'
  }

  return '/portal'
}
